import api from './api';

export interface TimerSession {
  id: number;
  user_id: number;
  start_time: string;
  pause_start?: string;
  is_paused: boolean;
  total_pause_seconds: number;
  last_activity: string;
  created_at: string;
  elapsed_seconds: number;
}

export interface TimerStartRequest {
  latitude?: number;
  longitude?: number;
}

export interface TimerStopRequest {
  project_id: number;
  description?: string;
  latitude?: number;
  longitude?: number;
}

export interface TimeEntry {
  id: number;
  project_id: number;
  user_id: number;
  start_time: string;
  end_time: string;
  break_duration: number;
  description: string;
  date: string;
  start_latitude?: number;
  start_longitude?: number;
  end_latitude?: number;
  end_longitude?: number;
  created_at: string;
  updated_at: string;
  project_name?: string;
  user_name?: string;
}

export interface ActiveTimerSession {
  id: number;
  user_id: number;
  user_name: string;
  start_time: string;
  start_location?: string;
  start_latitude?: number;
  start_longitude?: number;
  is_paused: boolean;
  elapsed_seconds: number;
  last_activity: string;
}

class TimerAPIService {
  // Get current timer status
  async getStatus(): Promise<TimerSession | null> {
    try {
      const response = await api.get<TimerSession>('/timer/status');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No active timer
      }
      throw error;
    }
  }

  // Start a new timer
  async start(startRequest?: TimerStartRequest): Promise<TimerSession> {
    const response = await api.post<TimerSession>('/timer/start', startRequest || {});
    return response.data;
  }

  // Toggle pause/resume
  async togglePause(): Promise<TimerSession> {
    const response = await api.post<TimerSession>('/timer/pause');
    return response.data;
  }

  // Stop timer and create time entry
  async stop(stopRequest: TimerStopRequest): Promise<TimeEntry> {
    const response = await api.post<TimeEntry>('/timer/stop', stopRequest);
    return response.data;
  }

  // Get all active timer sessions (Admin and Bauleiter only)
  async getActiveSessions(): Promise<ActiveTimerSession[]> {
    const response = await api.get<ActiveTimerSession[]>('/timer/active-sessions');
    return response.data;
  }

  // Stop timer for another user (Admin and Bauleiter only)
  async adminStopTimer(userId: number): Promise<{ 
    message: string; 
    user_name: string;
    project_name?: string;
    worked_hours?: number;
    break_minutes?: number;
    time_entry_id?: number;
    stopped_by?: string;
  }> {
    const response = await api.post<{ 
      message: string; 
      user_name: string;
      project_name?: string;
      worked_hours?: number;
      break_minutes?: number;
      time_entry_id?: number;
      stopped_by?: string;
    }>(`/timer/admin-stop/${userId}`);
    return response.data;
  }
}

export const timerAPI = new TimerAPIService();
export default timerAPI;